/**
 * CSV Export Utilities
 *
 * Shared utilities for exporting data to CSV format.
 * Used by DataTable components in both client and bcc apps.
 */

/**
 * Downloads a CSV string as a file
 */
export const downloadCsv = (data: string, filenamePrefix = "export") => {
  const filename = `${filenamePrefix}-${new Date().toISOString().split("T")[0]}.csv`;
  const blob = new Blob(["\uFEFF", data], { type: "text/csv;charset=utf-8;" });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};

/**
 * Checks if a value is a plain object (not an array, date, etc.)
 */
export const isPlainObject = (v: unknown): v is Record<string, unknown> => Object.prototype.toString.call(v) === "[object Object]";

/**
 * Sanitizes an object value to a string representation
 */
export const sanitizeObject = (val: object): string => {
  try {
    const obj = val as { toString?: () => string };
    if (obj.toString && obj.toString !== Object.prototype.toString) {
      const strVal = obj.toString();
      if (strVal !== "[object Object]") {
        return strVal;
      }
    }
  } catch {
    // Ignore toString errors
  }

  try {
    return JSON.stringify(val);
  } catch {
    // Ignore stringify errors
  }

  return `[${Object.prototype.toString.call(val).slice(8, -1)}]`;
};

/**
 * Sanitizes any value to a primitive type suitable for CSV export
 */
export const sanitizeValue = (val: unknown): string | number | boolean | null | undefined => {
  if (val == null) {
    return val;
  }
  if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") {
    return val;
  }
  if (val instanceof Date) {
    return val.toISOString();
  }
  if (Array.isArray(val)) {
    return val
      .map((item) => {
        const sanitized = sanitizeValue(item);
        return sanitized == null ? "" : String(sanitized);
      })
      .filter(Boolean)
      .join(", ");
  }

  if (val !== null && typeof val === "object") {
    return sanitizeObject(val);
  }

  return `[${Object.prototype.toString.call(val).slice(8, -1)}]`;
};

/**
 * Type for a flattened object with primitive values
 */
export type FlattenedObject = Record<string, string | number | boolean | null | undefined>;

/**
 * Flattens a nested object into a single-level object with dot-notation keys
 *
 * @example
 * flattenObject({ user: { name: 'John', age: 30 } })
 * // Returns: { 'user.name': 'John', 'user.age': 30 }
 */
export const flattenObject = (obj: Record<string, unknown>, prefix = ""): FlattenedObject => {
  return Object.entries(obj).reduce<FlattenedObject>((acc, [key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value && isPlainObject(value)) {
      return {
        ...acc,
        ...flattenObject(value, path),
      };
    } else {
      acc[path] = sanitizeValue(value);
    }
    return acc;
  }, {});
};

/**
 * Creates a column visibility filter function
 *
 * @param visibleColumns - Whitelist of columns to include (if provided)
 * @param hiddenColumns - Blacklist of columns to exclude (fallback)
 * @param defaultHiddenColumns - Default columns to always hide
 */
export const createColumnVisibilityFilter = (
  visibleColumns?: string[],
  hiddenColumns?: string[],
  defaultHiddenColumns: string[] = []
): ((key: string) => boolean) => {
  return (key: string) => {
    if (visibleColumns) {
      // Whitelist: only include columns that match or are nested under visible columns
      return visibleColumns.some(
        (visibleCol) => key === visibleCol || key.startsWith(`${visibleCol}.`) || visibleCol.startsWith(`${key}.`)
      );
    } else {
      // Blacklist: exclude hidden columns (backward compatibility)
      const allHidden = [...(hiddenColumns ?? []), ...defaultHiddenColumns];
      return !allHidden.some((hiddenCol) => key === hiddenCol || key.startsWith(`${hiddenCol}.`));
    }
  };
};
